import { supabase } from '../../../lib/supabase'
import { COLORS_BY_ID, type ColorId } from '../data/colors'

export interface AiColorPickArgs {
  personaName: string
  personaDescription: string
  subject: string
  /** Colors the AI still has in hand — one of these must be returned. */
  availableColorIds: ColorId[]
  /** Optional Claude model override; server decides if key is unset. */
  model?: string
}

export interface AiColorPickResult {
  colorId: ColorId
  reasoning?: string
  source: 'api' | 'fallback'
}

/**
 * Call the pick-colorful-color edge function to get an AI's color pick.
 * If the function / network / model errors, falls back to a local random
 * pick so the UI never hangs on a broken backend.
 */
export async function requestAiColorPick(args: AiColorPickArgs): Promise<AiColorPickResult> {
  const available = args.availableColorIds.map(id => {
    const c = COLORS_BY_ID[id]
    return { id: c.id, name: c.name, hex: c.hex }
  })

  try {
    const { data, error } = await supabase.functions.invoke('pick-colorful-color', {
      body: {
        persona_name: args.personaName,
        persona_description: args.personaDescription,
        subject: args.subject,
        available_colors: available,
        model: args.model,
      },
    })

    if (error) throw error
    const picked = args.availableColorIds.find(id => id === data?.color_id)
    if (!picked) throw new Error(`Server returned unknown color: ${data?.color_id}`)

    const source = data?.source === 'api' ? 'api' : 'fallback'
    const reason = typeof data?.reason === 'string' ? data.reason : ''
    console.log('[colorful-ai] color subject=%o persona=%o → %s source=%s%s',
      args.subject, args.personaName, COLORS_BY_ID[picked].name, source,
      reason ? ` reason=${reason}` : '')

    return {
      colorId: picked,
      reasoning: typeof data?.reasoning === 'string' ? data.reasoning : undefined,
      source,
    }
  } catch (err) {
    console.warn('[colorful-ai] color call failed, random fallback:', err)
    const idx = Math.floor(Math.random() * args.availableColorIds.length)
    return { colorId: args.availableColorIds[idx], source: 'fallback' }
  }
}
